import { createContext, useContext, useMemo } from 'react';
import { defaultIcons } from '../icons';
import type {
  ChatSurfaceContextValue,
  ChatSurfaceProviderProps,
} from './types';

const ChatSurfaceContext = createContext<ChatSurfaceContextValue | null>(null);

/**
 * Provides current user, callbacks, theme tokens, and overridable icons to
 * every chat-surface component. The main app supplies a value derived from
 * its existing FirebaseAuth/Theme containers; the embed app (PR 9+) supplies
 * one derived from useEmbedSession().
 */
export function ChatSurfaceProvider({
  currentUser = null,
  callbacks = {},
  theme = {},
  icons,
  mode = 'main',
  children,
}: ChatSurfaceProviderProps) {
  const value: ChatSurfaceContextValue = useMemo(
    () => ({
      currentUser,
      callbacks,
      theme,
      icons: { ...defaultIcons, ...(icons ?? {}) },
      mode,
    }),
    [currentUser, callbacks, theme, icons, mode],
  );
  return (
    <ChatSurfaceContext.Provider value={value}>
      {children}
    </ChatSurfaceContext.Provider>
  );
}

/**
 * Read the chat-surface context. Falls back to defaults when no provider is
 * mounted — so individual components can be unit-tested without wrapping in
 * a provider. Production usage should always mount the provider.
 */
export function useChatSurfaceContext(): ChatSurfaceContextValue {
  const ctx = useContext(ChatSurfaceContext);
  if (ctx) return ctx;
  return {
    currentUser: null,
    callbacks: {},
    theme: {},
    icons: defaultIcons,
    mode: 'main',
  };
}
